import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useSelector } from "react-redux";
import { selectAuthState } from "@web-src/modules/auth/store/authSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  EntitiesListContext,
  EntitiesListContextValue,
} from "./EntitiesListContext";

export const EntitiesListProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const { entitiesApi } = useRestApiProvider();
  const {
    data: entities,
    isLoading: isEntitiesLoading,
    isFetching: isEntitiesFetching,
    isError: isEntitiesError,
    refetch: refetchEntities,
  } = entitiesApi.use_DEPRECATED_getAllOrgEntitiesListQuery(
    isAuthenticated ? undefined : skipToken
  );

  const {
    data: entityInvites,
    isLoading: isEntityInvitesLoading,
    isFetching: isEntityInvitesFetching,
    isError: isEntityInvitesError,
    refetch: refetchEntityInvites,
  } = entitiesApi.useGetEntityInvitesQuery(
    isAuthenticated ? undefined : skipToken
  );

  const refetchEntitiesAsPromise = useCallback(async () => {
    await refetchEntities();
  }, [refetchEntities]);

  const refetchEntityInvitesAsPromise = useCallback(async () => {
    await refetchEntityInvites();
  }, [refetchEntityInvites]);

  const value: EntitiesListContextValue = useMemo(
    () => ({
      entitiesState: {
        isLoading: isEntitiesLoading,
        isFetching: isEntitiesFetching,
        isError: isEntitiesError,
        entities,
      },
      entityInvitesState: {
        isLoading: isEntityInvitesLoading,
        isFetching: isEntityInvitesFetching,
        isError: isEntityInvitesError,
        entityInvites,
      },
      refetchEntities: refetchEntitiesAsPromise,
      refetchEntityInvites: refetchEntityInvitesAsPromise,
    }),
    [
      isEntitiesLoading,
      isEntitiesFetching,
      isEntitiesError,
      entities,
      isEntityInvitesLoading,
      isEntityInvitesFetching,
      isEntityInvitesError,
      entityInvites,
      refetchEntitiesAsPromise,
      refetchEntityInvitesAsPromise,
    ]
  );

  return (
    <EntitiesListContext.Provider value={value}>
      {children}
    </EntitiesListContext.Provider>
  );
};
